/**
 * Route-level suppression: the second, coarser layer under the per-surface gates.
 *
 * Component gates are precise but they only protect what somebody remembered to wrap. This layer
 * protects whole destinations whose PURPOSE is a surface the platform forbids, so that even a
 * fully ungated page cannot be reached in a build where it must not exist. Two layers, because
 * the cost of one miss is a store takedown and the cost of this file is thirty lines.
 *
 * Matching is on the route SUFFIX, not the full path, and that detail is deliberate. Nuvra
 * registers most destinations twice, at `/creator` and at `/app/creator`, and a table keyed on
 * full paths would silently protect one and not the other. Suffix matching means a route added
 * under any future prefix is covered the day it appears.
 *
 * On the web this is an identity wrapper. It renders children, always.
 */

import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { currentBillingPolicy, type PaymentSurface } from "./billingPolicy";

/**
 * Destinations that exist to move money, mapped to the surface that justifies them.
 *
 * Only routes whose PRIMARY function is the named surface belong here. A page that merely
 * contains a payment call to action among other useful things (the marketplace, which is worth
 * browsing; the billing page, which shows real usage) stays reachable and gates the call to
 * action itself. Blocking those wholesale would remove working features from Android users for
 * no policy benefit, which is a worse product, not a safer one.
 *
 * Creator Studio qualifies: earnings, payout schedule, payout method and withdrawal history are
 * seller money-out mechanics end to end, and the payout journey is exactly the hosted external
 * billing flow that must not appear inside the Play build.
 */
const RESTRICTED_ROUTES: ReadonlyArray<{ suffix: string; surface: PaymentSurface; title: string }> = [
  { suffix: "creator", surface: "payout_management", title: "Creator Studio" },
];

/** Trailing slashes and the `/app` prefix removed, so `/app/creator/` and `/creator` are one thing. */
export function routeSuffix(pathname: string): string {
  return pathname.replace(/\/+$/, "").replace(/^\/app(?=\/|$)/, "").replace(/^\/+/, "");
}

/** Pure, so the table can be asserted in tests without mounting a router. */
export function restrictionFor(pathname: string, policy = currentBillingPolicy()) {
  const suffix = routeSuffix(pathname);
  const match = RESTRICTED_ROUTES.find(r => suffix === r.suffix || suffix.startsWith(`${r.suffix}/`));
  if (!match) return null;
  return policy.allows(match.surface) ? null : match;
}

export function PlatformRouteGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  const policy = currentBillingPolicy();
  const blocked = restrictionFor(location.pathname, policy);

  if (!blocked) return <>{children}</>;

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-6">
      <div className="max-w-sm text-center">
        <h1 className="text-base font-medium text-foreground">{blocked.title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{policy.reasonFor(blocked.surface)}</p>
      </div>
    </div>
  );
}
